import * as React from 'react';

import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ManagePointCategoryContainer } from '@/features/points-management/point-setup-forms/customer/ManagePointCategoryContainer';
import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { CustomerPointSettingListResponse } from '@/services/pointConfiguration/pointConfiguration.types';
import { useSearch } from '@tanstack/react-router';
import { getTitleByActiveTab } from '../../table/helper';
import useManageCustomerPointSettingStatus from '../hooks/useManageCustomerPointSettingStatus';
import { getColumns } from './Column';

export function CustomerPointsConfigurationTable({
  data,
  isLoading,
  pagination,
}: {
  data: CustomerPointSettingListResponse[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { activeTab } = useSearch({ from: '/(app)/points-management/points-configuration/' });
  const { t } = useDynamicLocale();
  const { mutateAsync: updateStatus } = useManageCustomerPointSettingStatus();
  const canViewReferralStageList = useCan({ allOf: [PERMS.point.management.referralStage.list] });

  const columns = React.useMemo(
    () => getColumns(onOpen, openConfirmation, t, updateStatus, canViewReferralStageList),
    [onOpen, openConfirmation, t, updateStatus, canViewReferralStageList],
  );
  const title = getTitleByActiveTab(activeTab ?? 'customer', t);

  const onAddnew = () => {
    onOpen({
      modalTitle: 'pointManagement.page.form.manageSlabs.title',
      title: t('pointManagement.page.form.manageSlabs.title'),
      okText: t('common.page.button.submit'),
      size: 'lg',
      cancelText: t('common.page.button.cancel'),
      component: <ManagePointCategoryContainer />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pagination.totalPages,
    initialState: {
      sorting: [{ id: 'createdDate', desc: true }],
    },
    meta: {
      title,
      action: onAddnew,
      actionKey: 'onAddNew',
      actionDisabled: true,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => originalRow.programId,
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable className="rounded-t-none" table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
